import { useCallback, useRef } from "react";

const EDGE_THRESHOLD = 48;
const MAX_SCROLL_SPEED = 16;

/**
 * Scrolls the given container while a drag pointer sits near its top/bottom
 * edge, so long tab lists that overflow the dialog can still be reordered
 * end-to-end without the user manually scrolling mid-drag.
 *
 * dragover only tells us the pointer's latest position — it doesn't fire on
 * every frame, so driving scrollTop directly from it looks choppy. Instead
 * dragover just updates the desired speed, and a requestAnimationFrame loop
 * applies it every frame for smooth motion.
 */
export const useAutoScroll = () => {
  const containerRef = useRef<HTMLElement | null>(null);
  const speedRef = useRef(0);
  const rafIdRef = useRef<number | null>(null);

  const tick = useCallback(() => {
    const container = containerRef.current;
    if (container && speedRef.current !== 0) {
      container.scrollTop += speedRef.current;
      rafIdRef.current = requestAnimationFrame(tick);
    } else {
      rafIdRef.current = null;
    }
  }, []);

  const handleDragOverAutoScroll = useCallback(
    (clientY: number) => {
      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const distanceFromTop = clientY - rect.top;
      const distanceFromBottom = rect.bottom - clientY;

      let speed = 0;
      if (distanceFromTop >= 0 && distanceFromTop < EDGE_THRESHOLD) {
        speed = -MAX_SCROLL_SPEED * (1 - distanceFromTop / EDGE_THRESHOLD);
      } else if (distanceFromBottom >= 0 && distanceFromBottom < EDGE_THRESHOLD) {
        speed = MAX_SCROLL_SPEED * (1 - distanceFromBottom / EDGE_THRESHOLD);
      }

      speedRef.current = speed;
      if (speed !== 0 && rafIdRef.current === null) {
        rafIdRef.current = requestAnimationFrame(tick);
      }
    },
    [tick],
  );

  const stopAutoScroll = useCallback(() => {
    speedRef.current = 0;
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  }, []);

  return { containerRef, handleDragOverAutoScroll, stopAutoScroll } as const;
};
